import { useMutation } from '@tanstack/react-query';
import { useTimeZone } from '@/auth/useTimeZone.js';
import { StartOrJoinSession } from '@/components/session/start-or-join-session.js';
import { Button } from '@/components/ui/button.js';
import { Card, CardHeader, CardTitle } from '@/components/ui/card.js';
import { getSessionFormattedSchedule, getTeachingCoachId } from '@airreading/common';
import { DayOfWeekLabelMap, SortedDaysOfWeek, type StudentSession } from '@airreading/common/data';
import { attendSession } from '@/lib/api-calls.js';
import { useErrorToast } from '@/lib/error-handling.js';
import unionIcon from '@/assets/icons/union-icon.svg';
import teacherIcon from '@/assets/icons/teacher-icon.svg';

interface StudentSessionCardProps {
  studentSession: StudentSession;
  studentName: string;
  isFuture?: boolean;
  index: number;
}

export const StudentSessionCard = ({ studentSession, studentName, isFuture, index }: StudentSessionCardProps) => {
  const { timeZone } = useTimeZone();
  const { attendeeUrl, startInstant, coach, status, substitutionRequests } = studentSession;

  const { mutate: attendSessionMutate, error: attendSessionError } = useMutation({
    mutationFn: attendSession,
  });
  useErrorToast(attendSessionError, 'Failed to record your attendance for this class.');

  const coachIdWhoIsTeaching = getTeachingCoachId({
    coachId: coach?.id || null,
    status,
    substitutionRequests,
  });
  const isCurrentCoachTeaching = coach?.id === coachIdWhoIsTeaching;

  const substituteCoach = substitutionRequests.find(
    (req) => req.substituteId === coachIdWhoIsTeaching,
  )?.substituteCoach;

  const previousCoachDisplayName = coach?.coachName ?? '-';
  const currentCoachDisplayName = isCurrentCoachTeaching ? previousCoachDisplayName : substituteCoach?.coachName ?? '-';

  const plainDate = startInstant.toZonedDateTimeISO(timeZone).toPlainDate();
  const startDayOfWeek = SortedDaysOfWeek[plainDate.dayOfWeek - 1];
  const { sessionTimeFormatted } = getSessionFormattedSchedule({
    ...studentSession,
    displayTimeZone: timeZone,
  });

  const zonedDateTime = startInstant.toZonedDateTimeISO(timeZone);
  const date = zonedDateTime.toPlainDate();

  const monthDayFormatted = `${date.toLocaleString('en-US', { month: 'short' })} ${date.day}`;
  const classDateLabel = `${DayOfWeekLabelMap[startDayOfWeek]?.abbr}, ${monthDayFormatted}`;
  const joinClassAriaLabel = `Join class, ${classDateLabel}, ${sessionTimeFormatted}`;

  const onStartOrJoinSessionButtonClick = () => {
    attendSessionMutate({ id: studentSession.sessionId });
  };

  const cardTestId = `student-session-card-${index + 1}`;
  const dateTestId = `${cardTestId}-date`;
  const timeTestId = `${cardTestId}-time`;
  const studentTestId = `${cardTestId}-student`;
  const teacherTestId = `${cardTestId}-teacher`;
  const joinButtonTestId = `${cardTestId}-button-join`;
  const labelIdPrefix = `student-session-${isFuture ? 'future' : 'upcoming'}-${studentSession.sessionId}`;
  const dateLabelId = `${labelIdPrefix}-date`;
  const timeLabelId = `${labelIdPrefix}-time`;

  const nameTextClassName =
    'my-2 min-w-0 break-words font-normal text-base leading-6 tracking-[-0.75%] text-left capitalize ml-2 text-gray-600';

  return (
    <Card className="w-full max-w-[300px] min-w-0" role="article" aria-labelledby={`${dateLabelId} ${timeLabelId}`}>
      <CardHeader className={isFuture ? 'px-6 pt-6 pb-3' : 'p-6'}>
        <CardTitle>
          <h3
            id={dateLabelId}
            data-testid={dateTestId}
            className="text-2xl font-semibold leading-none tracking-tight text-wrap"
          >
            {classDateLabel}
          </h3>
        </CardTitle>
        <div>
          <p id={timeLabelId} className="my-2 text-lg font-semibold lowercase text-wrap" data-testid={timeTestId}>
            {sessionTimeFormatted}
          </p>
          <div className={`mt-2 ${isFuture ? 'mb-4' : ''}`}>
            <p className="flex items-start min-w-0">
              <img src={unionIcon} alt="" aria-hidden="true" className="w-5 h-5 mt-2 shrink-0" />
              <span className={nameTextClassName}>
                <span className="sr-only">Student: </span>
                <span data-testid={studentTestId}>{studentName}</span>
              </span>
            </p>
            <p className="flex items-start min-w-0">
              <img src={teacherIcon} alt="" aria-hidden="true" className="w-5 h-5 mt-2 shrink-0" />
              <span className={nameTextClassName}>
                <span className="sr-only">Teacher: </span>
                <span data-testid={teacherTestId}>{currentCoachDisplayName}</span>
              </span>
            </p>
          </div>
          {!isFuture && (
            <StartOrJoinSession
              label="Join class"
              url={attendeeUrl}
              whenUrlEmptyRender={
                <Button disabled className="h-auto min-h-11 w-full whitespace-normal">
                  No valid meeting link available
                </Button>
              }
              startButtonProps={{
                onClick: onStartOrJoinSessionButtonClick,
                className:
                  'bg-air-primary hover:bg-air-primary-hover active:bg-air-primary-active rounded-sm h-auto min-h-11 w-full py-3 px-4',
                icon: undefined,
                'data-testid': joinButtonTestId,
                'aria-label': joinClassAriaLabel,
              }}
            />
          )}
        </div>
      </CardHeader>
    </Card>
  );
};
